/**
 * Rating Component
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  VariableSelectValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseSpacingFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableSelect,
  formatSpacingFormat,
  formatTextAlignmentFormat,
  formatColorFormat,
  formatResponsiveValue,
} from '../../format-utils'
import ratingTemplate from './rating.template.json'

export interface IRatingComponentSettings {
  // Data settings
  'rating-data-value': VariableSelectValue
  'rating-data-count': number[]
  'rating-data-interval': number[]

  // Design settings
  'rating-design-h-align': ResponsiveValue<TextAlignment | 'parent'>
  'rating-design-size': ResponsiveValue<string>
  'rating-design-margin': ResponsiveValue<SpacingInputValue>
  'rating-design-color': ColorAdaptiveValue<string>
  'rating-design-color-solid': ColorAdaptiveValue<ColorVariant>
  'rating-design-variant': string
  'rating-design-width': number[]
}

export interface IRatingComponent extends IBuilderComponent {
  componentId: 'rating'
  groupId: 'interactive'
  settings: IRatingComponentSettings
}

/**
 * Rating AI Input
 */
export interface AiRatingInput {
  value?: string | number // Default rating value
  count?: number // Number of stars/items (default: 5)
  interval?: number // Interval between ratings (default: 1)
  alignment?: string // Format: "parent" or "center"
  size?: string // Format: "sm" or "md" or "lg" or "sm md:md de:lg"
  margin?: string // Format: "m:16px"
  color?: string // Format: "solid:primary-20" or "adaptive"
  variant?: string // Variant: "primary", "secondary", etc.
  width?: string // Format: "w:100%" -> [100]
}

const RATING_DEFAULT_TEMPLATE = ratingTemplate as unknown as IRatingComponentSettings

export class RatingComp {
  getTemplate(): IRatingComponentSettings {
    return RATING_DEFAULT_TEMPLATE
  }

  /**
   * format rating component settings to AI rating input
   */
  format(settings: IRatingComponentSettings): AiRatingInput {
    const result: AiRatingInput = {}

    // Data - Value (from VariableSelectValue)
    if (settings['rating-data-value']) {
      const value = formatFromVariableSelect(settings['rating-data-value'])
      if (value && value.startsWith('text:')) {
        const numValue = parseFloat(value.substring(5))
        result.value = isNaN(numValue) ? value.substring(5) : numValue
      } else {
        result.value = value
      }
    }

    // Data - Count
    if (settings['rating-data-count'] && settings['rating-data-count'].length > 0) {
      result.count = settings['rating-data-count'][0]
    }

    // Data - Interval
    if (settings['rating-data-interval'] && settings['rating-data-interval'].length > 0) {
      result.interval = settings['rating-data-interval'][0]
    }

    // Design - Alignment
    if (settings['rating-design-h-align']) {
      result.alignment = formatTextAlignmentFormat(settings['rating-design-h-align'] as any)?.replace(/^t:/, '') || undefined
    }

    // Design - Size
    if (settings['rating-design-size']) {
      const allSame = Object.values(settings['rating-design-size']).every((v) => v === Object.values(settings['rating-design-size'])[0])
      if (allSame) {
        result.size = Object.values(settings['rating-design-size'])[0]
      } else {
        result.size = formatResponsiveValue(settings['rating-design-size'], (val) => String(val))
      }
    }

    // Design - Margin
    if (settings['rating-design-margin']) {
      result.margin = formatSpacingFormat(settings['rating-design-margin'])
    }

    // Design - Color
    if (settings['rating-design-color']) {
      result.color = formatColorFormat({
        colorType: settings['rating-design-color'] as any,
        colorSolid: settings['rating-design-color-solid'],
      })
    }

    // Design - Variant
    if (settings['rating-design-variant']) {
      result.variant = settings['rating-design-variant']
    }

    // Design - Width
    if (settings['rating-design-width'] && settings['rating-design-width'].length > 0) {
      result.width = `w:${settings['rating-design-width'][0]}%`
    }

    return result
  }

  /**
   * parse AI rating input to rating component settings
   */
  parse(input: AiRatingInput): IRatingComponentSettings {
    const result = deepMerge(RATING_DEFAULT_TEMPLATE, {})

    // Data - Value
    if (input.value !== undefined) {
      result['rating-data-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.value),
      }
    }

    // Data - Count
    if (input.count !== undefined) {
      result['rating-data-count'] = [input.count]
    }

    // Data - Interval
    if (input.interval !== undefined) {
      result['rating-data-interval'] = [input.interval]
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentMap: Record<string, TextAlignment | 'parent'> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.alignment] || 'parent'
      result['rating-design-h-align'] = {
        phone: alignment as TextAlignment,
        'phone-landscape': alignment as TextAlignment,
        tablet: alignment as TextAlignment,
        'tablet-landscape': alignment as TextAlignment,
        desktop: alignment as TextAlignment,
        tv: alignment as TextAlignment,
      }
    }

    // Design - Size
    if (input.size) {
      // Parse responsive format for size
      const sizeMap: Record<string, string> = {
        sm: 'sm',
        md: 'md',
        lg: 'lg',
      }
      const parts = input.size.split(/\s+/)
      const sizeObj: ResponsiveValue<string> = {
        phone: 'md',
        'phone-landscape': 'md',
        tablet: 'md',
        'tablet-landscape': 'md',
        desktop: 'md',
        tv: 'md',
      }
      
      // Parse breakpoint-prefixed sizes: "sm md:md de:lg"
      for (const part of parts) {
        if (part.includes(':')) {
          const [breakpoint, size] = part.split(':')
          const bpMap: Record<string, keyof ResponsiveValue<string>> = {
            p: 'phone',
            pl: 'phone-landscape',
            t: 'tablet',
            tl: 'tablet-landscape',
            de: 'desktop',
            tv: 'tv',
          }
          const bp = bpMap[breakpoint]
          if (bp && sizeMap[size]) {
            sizeObj[bp] = sizeMap[size]
          }
        } else if (sizeMap[part]) {
          // Default size for all breakpoints
          Object.keys(sizeObj).forEach((key) => {
            sizeObj[key as keyof ResponsiveValue<string>] = sizeMap[part]
          })
        }
      }
      
      result['rating-design-size'] = sizeObj
    }

    // Design - Margin
    if (input.margin) {
      result['rating-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Color
    if (input.color) {
      const colorParsed = parseColorFormat(input.color)
      result['rating-design-color'] = colorParsed.colorType
      result['rating-design-color-solid'] = colorParsed.colorSolid
    }

    // Design - Variant
    if (input.variant) {
      result['rating-design-variant'] = input.variant
    }

    // Design - Width
    if (input.width) {
      // Parse width format and extract phone value as number array
      // Format: "w:100%" -> [100]
      const widthMatch = input.width.match(/w:(\d+)/)
      if (widthMatch) {
        result['rating-design-width'] = [parseInt(widthMatch[1])]
      }
    }

    return result
  }
}

